import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { adminLogin } from "../lib/api";
import { useRole, ROLES } from "../context/RoleContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Lock, User as UserIcon, Loader2 } from "lucide-react";

export function AdminLoginDialog({ open, onOpenChange }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { setRole } = useRole();
  const navigate = useNavigate();

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (!username.trim() || !password) {
      setError("Both username and password are required.");
      return;
    }
    setSubmitting(true);
    try {
      await adminLogin(username.trim(), password);
      setRole(ROLES.ADMIN);
      toast.success(`Welcome, ${username.trim()}`);
      onOpenChange(false);
      setUsername("");
      setPassword("");
      navigate("/admin");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Invalid username or password.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-[#0B0C10]/95 backdrop-blur-2xl border-white/10 text-white p-0 overflow-hidden" data-testid="admin-login-dialog">
        <div className="absolute top-0 right-0 w-48 h-48 rounded-full blur-3xl bg-[#F07D00]/20 pointer-events-none" />
        <div className="relative p-7">
          <DialogHeader className="text-left mb-6">
            <div className="h-11 w-11 rounded-lg bg-[#F07D00]/15 border border-[#F07D00]/30 flex items-center justify-center mb-4 shadow-[0_0_24px_rgba(240,125,0,0.25)]">
              <ShieldCheck className="h-5 w-5 text-[#F07D00]" />
            </div>
            <DialogTitle className="font-heading text-2xl font-semibold tracking-tight text-white">Coordinator sign-in</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400 mt-1">
              Restricted to authorized travel coordinators of Carrey Transportation.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="label-eyebrow">Username</Label>
              <div className="relative mt-2">
                <UserIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Daniel"
                  className="glass-input pl-10"
                  autoFocus
                  data-testid="admin-username-input"
                />
              </div>
            </div>

            <div>
              <Label className="label-eyebrow">Password</Label>
              <div className="relative mt-2">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="glass-input pl-10"
                  data-testid="admin-password-input"
                />
              </div>
            </div>

            {error && (
              <div className="text-xs font-semibold text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="admin-login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="brand-btn w-full mt-2"
              data-testid="admin-login-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in...
                </>
              ) : (
                "Sign in to Operations"
              )}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
